import HistoryIcon from '@mui/icons-material/History';
import { Alert, Box, Button, Grid } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { getJobPresets } from 'api/jobPreset';
import { JobPreset } from 'interfaces/jobPreset';
import { Permission } from 'interfaces/permissionOption';
import { cloneDeep, filter, find, includes, isEmpty, keys, omit, pickBy } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useCasesParams } from 'utils/useCasesParams';
import { useCurrentLabId } from 'utils/useCurrentLab';
import { usePermissions } from 'utils/usePermissions';
import { useStainTypeIdToDisplayName } from 'utils/useStainTypeIdToDisplayName';
import { CalculateFeaturesPresetAutocomplete } from './CalculateFeaturesPresetAutocomplete';
import { getConsumerPresetJson } from './presetEncapsulation';
import { Action, PresetService, StainsActions } from './presetService';

import PresetStainModal from './PresetStainModal';
import { PresetVersionHistoryDialog } from './PresetVersionHistoryDialog';

interface PresetsStepProps {
  currentStains: string[];
  setCalculateFeatureParams: (params: any) => void;
  setPipRunName: (name: string) => void;
  defaultPresetFromPipRunName: string;
  currentPreset: JobPreset | null;
  setCurrentPreset: React.Dispatch<React.SetStateAction<JobPreset>>;
}

export const PresetsStep: React.FC<PresetsStepProps> = ({
  currentPreset,
  currentStains,
  defaultPresetFromPipRunName,
  setCurrentPreset,
  setCalculateFeatureParams,
  setPipRunName,
}) => {
  const [presetStainModalOpen, setPresetStainModalOpen] = useState(false);
  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false);
  const { stainTypeIdToDisplayName, isLoadingStainTypeOptions } = useStainTypeIdToDisplayName();
  const { casesParams } = useCasesParams();
  const { labId } = useCurrentLabId();
  const { hasPermission } = usePermissions();
  const canEditAdvancedActions = hasPermission(Permission.UseNucleaiExperimentalFeatures);

  const removeReactElementFromPreset = (preset?: JobPreset | null): JobPreset | null => {
    if (!preset) {
      return null;
    }

    return omit(preset, 'displayName') as JobPreset;
  };

  const { data: presets, isLoading: isLoadingPresets } = useQuery({
    queryKey: ['jobPresets', { steps: ['calculate_features'], labId }],
    queryFn: ({ signal }) => getJobPresets(['calculate_features'], signal),
  });

  useEffect(() => {
    if (!isEmpty(defaultPresetFromPipRunName) && !isLoadingPresets && !currentPreset && !isEmpty(presets)) {
      const presetToSelect = find(presets, (p) => p.id === defaultPresetFromPipRunName);
      if (presetToSelect) selectPreset(presetToSelect);
    }
  }, [defaultPresetFromPipRunName, isLoadingPresets, currentPreset, presets]);

  const setPresetOn = (stainsActions: StainsActions, preset?: JobPreset) => {
    const presetData = removeReactElementFromPreset(preset ?? currentPreset);

    if (!presetData) {
      return;
    }

    let newPreset = cloneDeep(presetData);

    const stainsToDelete = keys(pickBy(stainsActions, (stainAction) => stainAction.action === Action.DELETE));
    if (!isEmpty(stainsToDelete)) {
      newPreset = PresetService.deleteStains(presetData, stainsToDelete);
    }

    const stainsToReplace = pickBy(stainsActions, (stainAction) => stainAction.action === Action.REASSIGN);
    if (!isEmpty(stainsToReplace)) {
      newPreset = PresetService.replaceStains(newPreset, stainsToReplace);
    }

    setCalculateFeatureParams(getConsumerPresetJson(newPreset.presetJson, canEditAdvancedActions));

    if (newPreset.presetJson?.name) {
      setPipRunName(newPreset.presetJson?.name);
    }

    setPresetStainModalOpen(false);
  };

  const selectPreset = (preset: JobPreset) => {
    const presetWithoutCircularReferences = removeReactElementFromPreset(preset);

    if (!presetWithoutCircularReferences) {
      return;
    }

    setCurrentPreset(presetWithoutCircularReferences);
    const stainsInPresetNotInCurrent = filter(
      presetWithoutCircularReferences?.stains,
      (stain) => !includes(currentStains, stain)
    );

    if (!isEmpty(currentStains) && !isEmpty(stainsInPresetNotInCurrent)) {
      setPresetStainModalOpen(true);
    } else {
      setPresetOn({}, presetWithoutCircularReferences);
    }
  };

  const activePresets = filter(presets, (preset) => !preset?.deletedAt);
  const selectablePresets =
    currentPreset && !find(activePresets, { id: currentPreset.id }) ? [currentPreset, ...activePresets] : activePresets;

  return (
    <Grid>
      {!isLoadingPresets && isEmpty(selectablePresets) && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No presets are available for this lab. An administrator can publish or make presets global in Global Settings.
        </Alert>
      )}
      <Box sx={{ width: { xs: '100%', sm: 520 }, maxWidth: '100%' }}>
        <CalculateFeaturesPresetAutocomplete
          presets={selectablePresets}
          selectedPreset={currentPreset}
          isLoading={isLoadingPresets}
          groupContext={{ studyId: casesParams.studyId, labId }}
          stainTypeIdToDisplayName={stainTypeIdToDisplayName}
          isLoadingStainTypeOptions={isLoadingStainTypeOptions}
          onSelectPreset={selectPreset}
        />
      </Box>

      {currentPreset?.presetId && (
        <Grid container justifyContent="flex-end" sx={{ mt: 1 }}>
          <Button size="small" startIcon={<HistoryIcon />} onClick={() => setVersionHistoryOpen(true)}>
            Version history
          </Button>
        </Grid>
      )}

      {currentPreset?.presetId && (
        <PresetVersionHistoryDialog
          open={versionHistoryOpen}
          preset={currentPreset}
          onClose={() => setVersionHistoryOpen(false)}
          onLoadVersion={selectPreset}
          onVersionCreated={selectPreset}
        />
      )}

      {presetStainModalOpen && (
        <PresetStainModal
          currentStains={currentStains}
          presetStains={currentPreset?.stains}
          onFinish={(stainsActions: StainsActions) => setPresetOn(stainsActions)}
        />
      )}
    </Grid>
  );
};
